import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import { IStationTimingPairStatsDto } from "@ryde/shared";

import {
  IStationTimingPreviewSelection,
  IStationTimingPreviewStation,
  StationTimingOptionKindEnum,
  TStationTimingExpandedDayLegs,
  TStationTimingSelectionMap,
} from "./station-timing-preview.models";
import {
  buildStationTimingLegRows,
  buildStationTimingSelectionByKind,
  parseLegIndexFromOptionKey,
  resolveStationTimingSelection,
  splitStationTimingLegRowsByData,
} from "./station-timing-preview.utils";

interface IUseStationTimingPreviewParams {
  stations: IStationTimingPreviewStation[];
  plannedLegSeconds: (number | undefined)[];
  stats: IStationTimingPairStatsDto | undefined;
  onChange: (selection: IStationTimingPreviewSelection) => void;
}

export const useStationTimingPreview = ({
  stations,
  plannedLegSeconds,
  stats,
  onChange,
}: IUseStationTimingPreviewParams) => {
  const rows = useMemo(
    () => buildStationTimingLegRows({ stations, plannedLegSeconds, stats }),
    [stations, plannedLegSeconds, stats],
  );

  const { rowsWithData, rowsWithoutData } = useMemo(
    () => splitStationTimingLegRowsByData(rows),
    [rows],
  );

  const [selectionMap, setSelectionMap] = useState<TStationTimingSelectionMap>(
    {},
  );
  const [areNoDataLegsExpanded, setAreNoDataLegsExpanded] = useState(false);
  const [expandedDayLegs, setExpandedDayLegs] =
    useState<TStationTimingExpandedDayLegs>({});

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Keyed on the statistics rather than on `rows`: rows are rebuilt whenever the caller passes fresh
  // inline props, and re-seeding the defaults then would silently discard what the user picked.
  useEffect(() => {
    setSelectionMap(
      buildStationTimingSelectionByKind(
        rowsRef.current,
        StationTimingOptionKindEnum.Median,
      ),
    );
  }, [stats]);

  const selection = useMemo(
    () => resolveStationTimingSelection({ rows, selectionMap }),
    [rows, selectionMap],
  );

  useEffect(() => {
    onChange(selection);
  }, [selection, onChange]);

  const handleOptionChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const optionKey = event.target.value;
      setSelectionMap(previous => ({
        ...previous,
        [parseLegIndexFromOptionKey(optionKey)]: optionKey,
      }));
    },
  );

  const handleSelectAllMedian = useEvent((): void => {
    setSelectionMap(
      buildStationTimingSelectionByKind(
        rows,
        StationTimingOptionKindEnum.Median,
      ),
    );
  });

  const handleSelectAllAverage = useEvent((): void => {
    setSelectionMap(
      buildStationTimingSelectionByKind(
        rows,
        StationTimingOptionKindEnum.Average,
      ),
    );
  });

  // Lands every leg on its "planned" radio rather than on nothing: a radio group with no checked
  // member gives the user no visible answer to "what happens if I apply now".
  const handleClearSelection = useEvent((): void => {
    setSelectionMap(
      buildStationTimingSelectionByKind(
        rows,
        StationTimingOptionKindEnum.Planned,
      ),
    );
  });

  const handleNoDataLegsToggle = useEvent((): void => {
    setAreNoDataLegsExpanded(previous => !previous);
  });

  // Purely a display state: the day rows are informational, so expanding one changes nothing that the
  // selection, the counter or the bulk actions read.
  const handleDayBreakdownToggle = useEvent((legIndex: number): void => {
    setExpandedDayLegs(previous => ({
      ...previous,
      [legIndex]: !previous[legIndex],
    }));
  });

  const selectableLegsCount = rowsWithData.length;
  const selectedLegsCount = selection.selectedLegIndexes.length;

  return {
    rowsWithData,
    rowsWithoutData,
    selectionMap,
    selectableLegsCount,
    selectedLegsCount,
    areNoDataLegsExpanded,
    expandedDayLegs,
    handleOptionChange,
    handleSelectAllMedian,
    handleSelectAllAverage,
    handleClearSelection,
    handleNoDataLegsToggle,
    handleDayBreakdownToggle,
  };
};
